import { type AuditTrailEntryView } from '@vello/api-client';
import { useVocab } from './vocab';

/** The vocabulary that translates an audit action, by the source that wrote the entry. */
const ACTION_VOCABULARY: Readonly<Record<AuditTrailEntryView['source'], string>> = {
  AUDIT_LOG: 'audit.log_action',
  DISPATCH: 'dispatch.event_kind',
  OPERATOR_ADMIN: 'operator.audit_action',
  CONFIG_GOVERNANCE: 'audit.config_governance_action',
  ACCOUNT_ACTION: 'audit.account_action',
};

/** Reasons an entry can carry besides the problem sentences: the vocabularies of the sources' own reason codes, searched in order. */
const REASON_VOCABULARIES = [
  'ops.cancel_reason',
  'ride.cancel_reason',
  'operator.action_reason',
  'market.status_reason',
  'country_pack.retire_reason',
  'reputation.suspension_reason',
  'relationships.block_reason',
  'account.phone_change_reason',
  'account.rider_registration_reason',
  'settings.revision_reason',
  'geofence.service_area_reason',
] as const;

/**
 * D577: the words of one line of the global audit trail. The action is translated by the vocabulary its source declares, the
 * target by its kind (the identifier beside it stays an identifier), the reason through the catalog; the entry's free `detail`
 * object is never printed as `key: value` text — the cell says how many details were recorded and keeps them for support in
 * its tooltip.
 */
export function useAuditTrailText(): {
  readonly action: (e: AuditTrailEntryView) => string;
  readonly target: (e: AuditTrailEntryView) => string;
  readonly reason: (e: AuditTrailEntryView) => string;
} {
  const { v, has, reason } = useVocab();
  return {
    action: (e) => v(ACTION_VOCABULARY[e.source], e.action),
    target: (e) => (e.targetKind === undefined ? '—' : e.targetRef === undefined ? v('audit.target_kind', e.targetKind) : `${v('audit.target_kind', e.targetKind)} · ${e.targetRef}`),
    reason: (e) => {
      if (e.reasonCode === undefined) return '—';
      const code = e.reasonCode;
      const own = REASON_VOCABULARIES.find((id) => has(id, code));
      return own !== undefined ? v(own, code) : reason(code);
    },
  };
}
